import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useFileUpload } from '../../hooks/useFileUpload';
import MarkdownEditor from './MarkdownEditor';
import ResourceManager from './ResourceManager';
import QuizManager from './QuizManager';
import SafeIcon from '../../common/SafeIcon';
import LoadingSpinner from '../ui/LoadingSpinner';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiX, FiPlay, FiFileText, FiDownload, FiHelpCircle } = FiIcons;

const LessonEditor = ({ lesson, onSave, onCancel }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      title: lesson?.title || '',
      video_embed_url: lesson?.video_embed_url || '',
      text_content: lesson?.text_content || '',
      estimated_minutes: lesson?.estimated_minutes || 0,
      resources: lesson?.resources || []
    }
  });

  const [resources, setResources] = useState(lesson?.resources || []);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      await onSave({
        ...data,
        resources: resources
      });
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FiFileText },
    { id: 'content', label: 'Content', icon: FiFileText },
    { id: 'resources', label: 'Resources', icon: FiDownload },
    { id: 'quiz', label: 'Quiz', icon: FiHelpCircle }
  ];

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Tabs */}
        <div className="flex space-x-1 mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-nutrition-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg p-4 mb-4">
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lesson Title *
                </label>
                <input
                  {...register('title', { required: 'Title is required' })}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                  placeholder="Enter lesson title"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Video URL (YouTube/Vimeo)
                </label>
                <input
                  {...register('video_embed_url')}
                  type="url"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Minutes
                </label>
                <input
                  {...register('estimated_minutes', { valueAsNumber: true })}
                  type="number"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                  placeholder="0"
                />
              </div>
            </div>
          )}

          {activeTab === 'content' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lesson Content (Markdown supported)
              </label>
              <MarkdownEditor
                value={watch('text_content')}
                onChange={(value) => setValue('text_content', value)}
              />
            </div>
          )}

          {activeTab === 'resources' && (
            <ResourceManager
              resources={resources}
              onChange={setResources}
            />
          )}

          {activeTab === 'quiz' && lesson && (
            <QuizManager lessonId={lesson.id} />
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {saving ? (
              <LoadingSpinner size="sm" />
            ) : (
              <SafeIcon icon={FiSave} className="w-4 h-4" />
            )}
            <span>{saving ? 'Saving...' : 'Save Lesson'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default LessonEditor;